/**
 * Blocks the app on phone-sized screens, for the roles it is not built for.
 *
 * BNB India is a desktop tool — dense tables, multi-column forms, an admin that
 * assumes a mouse — so rather than ship a broken small-screen layout it shows a
 * plain message below a phone-sized viewport. Owners and company admins are
 * through it: they are the ones who need to approve something from a phone, and
 * the screens they need are the short ones.
 *
 * It used to wrap everything including login, which meant it refused before it
 * knew who was asking — so nobody could sign in on a phone to become somebody
 * allowed on a phone. It still wraps everything, because the block has to be
 * able to replace whatever is on screen; what changed is that it lets an
 * unidentified visitor through to the login form and decides once there is a
 * user to decide about. See shouldBlockMobile.
 *
 * Viewport-based, not user-agent: a real measurement is reliable, while UA
 * strings are spoofable and constantly out of date. It reacts to resize and
 * rotation, so a phone turned sideways is re-evaluated live — and, unlike
 * before, still recognised as a phone.
 */
import { useEffect, useState } from 'react';
import { LogOut, Monitor } from 'lucide-react';
import { shouldBlockMobile } from '../lib/mobileAccess';
import { ROLE_LABELS, useAuth } from '../store/auth';

function viewport(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 1280, height: 800 };
  return { width: window.innerWidth, height: window.innerHeight };
}

export default function MobileGate({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [size, setSize] = useState(viewport);

  useEffect(() => {
    const onResize = () => setSize(viewport());
    window.addEventListener('resize', onResize);
    // orientationchange fires ahead of resize on some mobile browsers, and
    // reading in both is cheaper than missing a rotation.
    window.addEventListener('orientationchange', onResize);
    // Sync once, in case the viewport changed between render and effect.
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  if (!shouldBlockMobile(user, size.width, size.height)) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6 text-center">
      <div className="max-w-sm">
        <Monitor className="mx-auto h-12 w-12 text-indigo-600" />
        <h1 className="mt-4 text-lg font-semibold text-gray-900">
          Please Use a Desktop Or Laptop
        </h1>
        {/* Names the role rather than saying "not supported". Somebody looking
            at this needs to know whether it is their device or their account,
            because the two have different answers — one is "find a computer"
            and the other is "this is not for you to do from here". */}
        <p className="mt-2 text-sm text-gray-600">
          BNB India isn’t available on mobile for{' '}
          <span className="font-medium text-gray-900">
            {user ? ROLE_LABELS[user.role] : 'your'}
          </span>{' '}
          accounts. Open it on a computer with a larger screen.
        </p>
        {/* Signed in behind this screen, and otherwise no way out of it: no
            navigation renders here, so without this the only exit is clearing
            the browser's storage. */}
        <button
          type="button"
          className="btn-ghost mx-auto mt-4 text-sm"
          onClick={() => {
            void logout();
          }}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}
